import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Table,
  Button,
  message,
  Tag,
  Drawer,
  Modal,
  Tooltip,
  Grid,
  Typography,
} from 'antd';
import {
  EditOutlined,
  LockOutlined,
  UnlockOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import {
  useCreateTeamUser,
  useDeleteTeamUser,
  useGetAllTeamUsers,
  useUpdateTeamUser,
} from './Hooks';
import { useAuthStore } from '../../store/useAuthStore';
import useDebounce from '../../utils/debounce';
import { SearchOutlined } from '@ant-design/icons';
import { validations } from '../../utils/validation';

const { Title } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;
const predefinedRoles = ['ADMIN', 'SUPER_ADMIN', 'READ_ONLY_ADMIN'];
const predefinedRolesTr = ['YÖNETİCİ', 'SÜPER_YÖNETİCİ', 'Sadece Okunur Yönetici'];

export default function TeamManagement() {
  const { t, i18n } = useTranslation('teamuser');
  const { user } = useAuthStore();
  const isReadOnlyUser = user?.role === 'READ_ONLY_ADMIN';

  const currentUserEmail = user?.email;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [loadingUserId, setLoadingUserId] = useState(null);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debouncedSearch = useDebounce(search, 500);
  const [loadingAction, setLoadingAction] = useState(null); // 'edit' | 'block' | 'delete'

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { data, isLoading, refetch } = useGetAllTeamUsers({
    offset: currentPage,
    limit: pageSize,
    search: debouncedSearch,
  });

  const users = data?.paginatedData || [];
  const totalUsers = data?.totalCount || 0;

  const createUserMutation = useCreateTeamUser({
    onSuccess: () => {
      message.success(t('userCreated'));
      refetch();
      setIsModalOpen(false);
      form.resetFields();
      setLoadingUserId(null);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('somethingWentWrong'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const updateUserMutation = useUpdateTeamUser({
    onSuccess: () => {
      message.success(t('userUpdated'));
      refetch();
      setLoadingUserId(null);
      setIsEditModalOpen(false);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('updateFailed'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const deleteUserMutation = useDeleteTeamUser({
    onSuccess: () => {
      message.success(t('userDeleted'));
      refetch();
      setLoadingUserId(null);
      setLoadingAction(null);
    },
    onError: (error) => {
      message.error(error?.response?.data?.message || t('deleteFailed'));
      setLoadingUserId(null);
      setLoadingAction(null);
    },
  });

  const handleAddUser = (values) => {
    if (isReadOnlyUser) return;
    const { phone, phone_code, ...rest } = values;
    createUserMutation.mutate({
      ...rest,
      phone: phone,
      phone_code: phone_code || '90',
    });
  };

  const handleUpdateUser = (values) => {
    if (isReadOnlyUser) return;

    // eslint-disable-next-line no-unused-vars
    const { phone, phone_code, role, email, ...updatedValues } = values;
    let payload;
    if (editUser.role === 'OPERATOR' || editUser.role === 'LOCATION_OWNER') {
      payload = { ...updatedValues, phone, phone_code: phone_code || '90' };
    } else if (editUser.role === 'SUPER_ADMIN') {
      // Do not send role if SUPER_ADMIN
      payload = { ...updatedValues, phone, phone_code: phone_code || '90' };
    } else {
      payload = { ...updatedValues, phone, phone_code: phone_code || '90', role };
    }
    setLoadingUserId(editUser._id);
    setLoadingAction('edit');
    updateUserMutation.mutate({ data: payload, id: editUser._id });
  };

  const handleEditUser = (record) => {
    if (isReadOnlyUser) return;
    setEditUser(record);
    editForm.setFieldsValue({
      fname: record.fname,
      lname: record.lname,
      email: record.email,
      phone: record.phone,
      phone_code: record.phone_code || '90',
      role: record.role,
      status: record.status,
    });
    setIsEditModalOpen(true);
  };
  const roleColors = {
    SUPER_ADMIN: 'gold',
    ADMIN: 'blue',
    READ_ONLY_ADMIN: 'cyan',
    OPERATOR: 'purple',
    LOCATION_OWNER: 'green', // Changed to green for better distinction
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [blockUnblockModalOpen, setBlockUnblockModalOpen] = useState(false);
  const [rfidToBlockUnblock, setRfidToBlockUnblock] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);

  const confirmBlockUnblockUser = (block) => {
    if (isReadOnlyUser) return;
    setLoadingUserId(rfidToBlockUnblock._id);
    setLoadingAction('block');
    const updatedStatus = block ? 'BLOCKED' : 'ACTIVE';
    const payload = {
      fname: rfidToBlockUnblock.fname,
      lname: rfidToBlockUnblock.lname,
      status: updatedStatus,
    };
    if (
      rfidToBlockUnblock.role !== 'OPERATOR' &&
      rfidToBlockUnblock.role !== 'LOCATION_OWNER'
    ) {
      payload.role = rfidToBlockUnblock.role;
    }
    updateUserMutation.mutate({
      id: rfidToBlockUnblock._id,
      data: payload,
    });
    setBlockUnblockModalOpen(false);
  };

  const confirmDeleteUser = () => {
    if (isReadOnlyUser) return;
    setLoadingUserId(userToDelete._id);
    setLoadingAction('delete');
    deleteUserMutation.mutate(userToDelete._id);
    setDeleteModalOpen(false);
  };

  const handleCloseCreateUserDrawer = () => {
    setIsModalOpen(false);
    form.resetFields(); // Reset the form fields
  };

  const handleCloseEditUserDrawer = () => {
    setIsEditModalOpen(false);
    editForm.resetFields(); // Reset the form fields
    setEditUser(null); // Clear the edit user state
  };

  const columns = [
    {
      title: t('firstName'),
      dataIndex: 'fname',
      key: 'fname',
      align: 'center',
    },
    {
      title: t('lastName'),
      dataIndex: 'lname',
      key: 'lname',
      align: 'center',
    },
    {
      title: t('email'),
      dataIndex: 'email',
      key: 'email',
      align: 'center',
      className: 'whitespace-nowrap',
      width: 350,
      render: (text) => (
        <Tooltip placement="topLeft" title={text}>
          {text}
        </Tooltip>
      ),
    },
    {
      title: t('role'),
      dataIndex: 'role',
      key: 'role',
      align: 'center',
      render: (role) => <Tag color={roleColors[role] || 'default'} variant="outlined">{role}</Tag>,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      render: (status) => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'red'} variant="outlined">{t(status)}</Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => {
        const isCurrentUser = record.email === currentUserEmail;
        const isBlocked = record.status === 'BLOCKED';

        const canManageAdminLikeRole =
          record?.role === 'ADMIN' ||
          record?.role === 'READ_ONLY_ADMIN' ||
          (record?.role === 'SUPER_ADMIN' && user?.role === 'SUPER_ADMIN');

        return (
          <div className="flex space-x-2">
            {/* Edit Button */}
            {canManageAdminLikeRole && (
              <Tooltip title={t('editUser')}>
                <Button
                  disabled={
                    isReadOnlyUser ||
                    (loadingUserId === record._id && loadingAction !== 'edit')
                  }
                  icon={<EditOutlined />}
                  loading={
                    loadingUserId === record._id && loadingAction === 'edit'
                  }
                  onClick={() => handleEditUser(record)}
                />
              </Tooltip>
            )}
            {/* Block/Unblock Button */}
            {canManageAdminLikeRole && (
              <Tooltip
                title={isBlocked ? t('userUnblockTitle') : t('userBlockTitle')}
              >
                <Button
                  disabled={
                    isReadOnlyUser ||
                    isCurrentUser ||
                    (loadingUserId === record._id && loadingAction !== 'block')
                  }
                  icon={isBlocked ? <UnlockOutlined /> : <LockOutlined />}
                  loading={
                    loadingUserId === record._id && loadingAction === 'block'
                  }
                  onClick={() => {
                    setRfidToBlockUnblock(record);
                    setBlockUnblockModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {/* Delete Button */}
            {canManageAdminLikeRole && (
              <Tooltip title={t('userDeleteTitle')}>
                <Button
                  danger
                  disabled={
                    isReadOnlyUser ||
                    isCurrentUser ||
                    (loadingUserId === record._id && loadingAction !== 'delete')
                  }
                  icon={<DeleteOutlined />}
                  loading={
                    loadingUserId === record._id && loadingAction === 'delete'
                  }
                  onClick={() => {
                    setUserToDelete(record);
                    setDeleteModalOpen(true);
                  }}
                />
              </Tooltip>
            )}
            {(record?.role === 'LOCATION_OWNER' || record?.role === 'OPERATOR') && (
              <div className='text-gray-500'>
                {record?.role === 'LOCATION_OWNER' ? t('manageFromLocationPage') : t('manageFromOperatorPage')}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {' '}
      <div className="flex justify-between items-center mb-4 gap-4">
        <Input
          style={{ width: 300 }}
          placeholder={t('searchPlaceholder')}
          prefix={<SearchOutlined />}
          value={search}
          allowClear
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1); // Reset to first page when searching
          }}
        />
        <Button
          type="primary"
          disabled={isReadOnlyUser}
          onClick={() => setIsModalOpen(true)}
        >
          {t('addUser')}
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={users}
        loading={isLoading}
        variant="outlined"
        size="small"
        tableLayout="fixed"
        scroll={{ x: 'max-content', y: 60 * 8.3 }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          size: 'default',
          total: totalUsers,
          onChange: (page, pageSize) => {
            setCurrentPage(page);
            setPageSize(pageSize);
          },
          showSizeChanger: false,
          position: ['bottomCenter'],
        }}
        rowKey="_id"
      />
      {isModalOpen && (
        <Drawer
          maskClosable={false}
          title={<Title level={4} className="mb-0!">{t('createUser')}</Title>}
          open={isModalOpen}
          onClose={handleCloseCreateUserDrawer} // Use the new handler
          width={isMobile ? '100%' : 600}
          destroyOnHidden
        >
          <Form form={form} layout="vertical" onFinish={handleAddUser}>
            <Form.Item
              label={t('firstName')}
              name="fname"
              rules={[
                { required: true, message: t('validation.firstNameRequired') },
                { max: 30, message: t('validation.max30Characters') },
                {
                  whitespace: true,
                  message: t('validation.noWhitespaceAllowed'), // Updated message
                },
              ]}
            >
              <Input placeholder={t('enterFirstName')} />
            </Form.Item>
            <Form.Item
              label={t('lastName')}
              name="lname"
              rules={[
                { required: true, message: t('validation.lastNameRequired') },
                { max: 30, message: t('validation.max30Characters') },
                {
                  whitespace: true,
                  message: t('validation.noWhitespaceAllowed'), // Updated message
                },
              ]}
            >
              <Input placeholder={t('enterLastName')} />
            </Form.Item>
            <Form.Item
              label={t('email')}
              name="email"
              rules={[
                { required: true, message: t('validation.emailRequired') },
                { type: 'email', message: t('validation.invalidEmail') },
                { max: 100, message: t('validation.max100Characters') },
              ]}
            >
              <Input placeholder={t('enterEmail')} />
            </Form.Item>
            <Form.Item
              label={t('phoneNumber')}
              name="phone"
              rules={[
                validations.phoneNumber(t('validation.invalidTurkishMobile')),
              ]}
            >
              <Input
                placeholder={t('enterPhoneNumber')}
                prefix="+90"
              />

            </Form.Item>
            <Form.Item
              label={t('role')}
              name="role"
              rules={[
                { required: true, message: t('validation.roleRequired') },
              ]}
            >
              <Select placeholder={t('selectRole')}>
                {predefinedRoles.map((role) => (
                  <Option key={role} value={role}>
                    {t(role)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <div className="flex justify-start space-x-2">
              <Button
                htmlType="submit"
                loading={createUserMutation.isPending}
                type="primary"
              >
                {t('submit')}
              </Button>
              <Button onClick={handleCloseCreateUserDrawer}>
                {t('cancel')}
              </Button>
            </div>
          </Form>
        </Drawer>
      )}
      <Drawer
        maskClosable={false}
        title={<Title level={4} className="mb-0!">{t('editUser')}</Title>}
        open={isEditModalOpen}
        destroyOnHidden
        onClose={handleCloseEditUserDrawer} // Use the new handler
        width={isMobile ? '100%' : 600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateUser}>
          <Form.Item
            label={t('firstName')}
            name="fname"
            rules={[
              { required: true, message: t('validation.firstNameRequired') },
              { max: 30, message: t('validation.max30Characters') },
              {
                whitespace: true,
                message: t('validation.noWhitespaceAllowed'), // Updated message
              },
            ]}
          >
            <Input placeholder={t('enterFirstName')} />
          </Form.Item>
          <Form.Item
            label={t('lastName')}
            name="lname"
            rules={[
              { required: true, message: t('validation.lastNameRequired') },
              { max: 30, message: t('validation.max30Characters') },
              {
                whitespace: true,
                message: t('validation.noWhitespaceAllowed'), // Updated message
              },
            ]}
          >
            <Input placeholder={t('enterLastName')} />
          </Form.Item>
          <Form.Item
            label={t('email')}
            name="email"
            rules={[
              { required: true, message: t('validation.emailRequired') },
              { type: 'email', message: t('validation.invalidEmail') },
              { max: 100, message: t('validation.max100Characters') },
            ]}
          >
            <Input disabled placeholder={t('enterEmail')} />
          </Form.Item>
          <Form.Item
            label={t('phoneNumber')}
            name="phone"
            rules={[
              validations.phoneNumber(t('validation.invalidTurkishMobile')),
            ]}
          >
            <Input
              placeholder={t('enterPhoneNumber')}
              addonBefore="+90"
            />
            <Form.Item noStyle name="phone_code" initialValue="90">
              <input type="hidden" />
            </Form.Item>
          </Form.Item>
          <Form.Item label={t('role')} name="role" rules={[{ required: true }]}>
            <Select disabled={true}>
              {(i18n.language === 'tr'
                ? predefinedRolesTr
                : predefinedRoles
              ).map((role) => (
                <Option key={role} value={role}>
                  {t(role)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('status')}
            name="status"
            rules={[{ required: true }]}
          >
            <Select disabled={editUser?.email === currentUserEmail}>
              <Option value="ACTIVE">{t('active')}</Option>
              <Option value="BLOCKED">{t('blocked')}</Option>
            </Select>
          </Form.Item>

          <div className="flex flex-row gap-2 justify-start">
            <Button
              htmlType="submit"
              loading={updateUserMutation.isPending}
              type="primary"
            >
              {t('update')}
            </Button>
            <Button onClick={handleCloseEditUserDrawer}>{t('cancel')}</Button>
          </div>
        </Form>
      </Drawer>
      <Modal
        confirmLoading={loadingUserId === rfidToBlockUnblock?._id}
        title={
          <Title level={4} className="mb-0!">
            {rfidToBlockUnblock?.status === 'ACTIVE'
              ? t('userBlockTitle')
              : t('userUnblockTitle')}
          </Title>
        }
        open={blockUnblockModalOpen}
        onCancel={() => setBlockUnblockModalOpen(false)}
        onOk={() =>
          confirmBlockUnblockUser(rfidToBlockUnblock?.status === 'ACTIVE')
        }
      >
        <p>
          {rfidToBlockUnblock?.status === 'ACTIVE'
            ? t('userBlockMessage', { name: rfidToBlockUnblock?.fname })
            : t('userUnblockMessage', { name: rfidToBlockUnblock?.fname })}
        </p>
      </Modal>
      <Modal
        confirmLoading={loadingUserId === userToDelete?._id}
        title={<Title level={4} className="mb-0!">{t('userDeleteTitle')}</Title>}
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={confirmDeleteUser}
      >
        <p>{t('userDeleteMessage', { name: userToDelete?.fname })}</p>
      </Modal>
    </div>
  );
}
